import { gql } from "@apollo/client";
import type {
  PermitStatus,
  PermitApplicationStatus,
  PermitDocumentReviewStatus,
  ISODateTime,
  PermitPayerAccount,
  PermitSubmittedByUser,
} from "common/types/permits";
import { registeredVehicleSummaryFragment } from "common/graphql/fragments";
import type { RegisteredVehicleSummary } from "common/graphql/fragments";

export const permitDetailFragment = gql`
  fragment permitDetailFragment on permit {
    id
    clientId: client_id
    displayId: display_id
    status
    applicationStatus: application_status
    validFrom: valid_from
    validTo: valid_to
    maxNumVehicles: max_num_vehicles
    applicationSubmittedAt: application_submitted_at
    applicationReviewedAt: application_reviewed_at
    index
    createdAt: created_at
    updatedAt: updated_at
    payerAccount: payer_account {
      id
      holderName: holder_name
      organizationName: organization_name
      ownerType: owner_type
      roleType: role_type
    }
    permitType: permit_type {
      id
      name
      description
    }
    applicationSubmittedByUser: application_submitted_by_user {
      id
      fullName: full_name
      email
      phoneNumber: phone_number
    }
    applicationApprovedByUser: application_approved_by_user {
      id
      email
      fullName: full_name
    }
    vehicles: permit_vehicles {
      permitId: permit_id
      registeredVehicleId: registered_vehicle_id
      accountId: account_id
      registeredVehicle: registered_vehicle {
        ...registeredVehicleSummaryFragment
      }
    }
    authority {
      name
    }
    documentProposals: document_proposals(order_by: { created_at: desc }) {
      id
      permitId: permit_id
      submittedAt: submitted_at
      reviewComment: review_comment
      reviewStatus: review_status
      reviewedAt: reviewed_at
      createdAt: created_at
      updatedAt: updated_at
      documents: documents {
        documentProposalId: document_proposal_id
        documentId: document_id
        reviewStatus: review_status
        reviewComment: review_comment
        approvedAt: approved_at
        createdAt: created_at
        updatedAt: updated_at
        document {
          id
          documentTypeId: document_type_id
          documentType: document_type {
            id
            name
            description
          }
          fileName: file_name
          s3Bucket: s3_bucket
          s3Path: s3_path
          validFrom: valid_from
          validTo: valid_to
          createdAt: created_at
        }
      }
    }
  }
  ${registeredVehicleSummaryFragment}
`;

export interface PermitDetailRaw {
  id: string;
  clientId: string;
  displayId: string;
  status: PermitStatus;
  applicationStatus: PermitApplicationStatus;
  validFrom: ISODateTime | null;
  validTo: ISODateTime | null;
  maxNumVehicles: number | null;
  applicationSubmittedAt: ISODateTime | null;
  applicationReviewedAt: ISODateTime | null;
  index: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  payerAccount: PermitPayerAccount | null;
  authority: { name: string } | null;
  permitType: {
    id: string;
    name: string;
    description: string;
  };
  applicationSubmittedByUser: PermitSubmittedByUser | null;
  applicationApprovedByUser: {
    id: string;
    email: string;
    fullName?: string;
  } | null;
  vehicles: {
    permitId: string;
    registeredVehicleId: string;
    accountId: string | null;
    registeredVehicle: RegisteredVehicleSummary;
  }[];
  documentProposals: {
    id: string;
    permitId: string;
    submittedAt: ISODateTime;
    reviewComment: string | null;
    reviewStatus: PermitDocumentReviewStatus;
    reviewedAt: ISODateTime | null;
    createdAt: ISODateTime;
    updatedAt: ISODateTime;
    documents: {
      documentProposalId: string;
      documentId: string;
      reviewStatus: PermitDocumentReviewStatus;
      reviewComment: string | null;
      approvedAt: ISODateTime | null;
      createdAt: ISODateTime;
      updatedAt: ISODateTime;
      document: {
        id: string;
        documentTypeId: string;
        documentType: {
          id: string;
          name: string;
          description: string | null;
        };
        fileName: string;
        s3Bucket: string;
        s3Path: string;
        validFrom: ISODateTime | null;
        validTo: ISODateTime | null;
        createdAt: ISODateTime;
      };
    }[];
  }[];
}
